import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { getAccessToken } from "../store/actions/auth";
import Loading from "../components/Loading";
import API_BASE_URL from "../config/baseUrl";

const CalendarIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zM184 392V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136H184z"></path>
  </svg>
);

const SwapRightIcon = () => (
  <svg
    viewBox="0 0 1024 1024"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M873.1 596.2l-164-208A32 32 0 00684 376h-64.8c-6.7 0-10.4 7.7-6.3 13l144.3 183H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h695.9c26.8 0 41.7-30.8 25.2-51.8z"></path>
  </svg>
);

const fetchAccountOperations = async function (userId) {
  try {
    const token = getAccessToken();
    if (!token) return { success: false, error: "No access token" };

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/get-account-operations",
      { userId: userId },
      { headers: { Authorization: "Bearer " + token } },
    );

    if (response.data.status === false) {
      return {
        success: false,
        error: response.data.message || "Failed to fetch",
      };
    }

    return { success: true, data: response.data.data || [] };
  } catch (err) {
    return {
      success: false,
      error: err.response?.data?.message || "Failed to fetch",
    };
  }
};

const AccountOperationsReportPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);

  useEffect(
    function () {
      const loadData = async function () {
        setLoading(true);
        const result = await fetchAccountOperations(userId || "");
        if (result.success) {
          setData(result.data);
        } else {
          setError(result.error);
        }
        setLoading(false);
      };
      loadData();
    },
    [userId],
  );

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-7xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-[#0F2327] px-3 sm:px-5 py-3 flex items-center justify-between flex-wrap gap-2">
              <span className="text-base sm:text-lg md:text-xl text-white font-light capitalize truncate flex-1">
                List Of All Transactions ({data.length})
              </span>
              <button
                onClick={function () {
                  navigate(-1);
                }}
                className="bg-blue-500 text-white px-3 sm:px-4 py-1 hover:bg-blue-600 text-sm sm:text-base whitespace-nowrap"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Date Range Picker */}
            <div className="p-3 sm:p-4">
              <div
                className="flex items-center border border-gray-300 bg-white w-full max-w-md"
                style={{ borderRadius: 0 }}
              >
                <div className="flex-1 px-2 py-2 min-w-0">
                  <input
                    type="text"
                    readOnly
                    placeholder="Start date"
                    className="w-full text-xs sm:text-sm focus:outline-none bg-transparent"
                  />
                </div>
                <div className="px-2 text-gray-400 flex-shrink-0">
                  <SwapRightIcon />
                </div>
                <div className="flex-1 px-2 py-2 min-w-0">
                  <input
                    type="text"
                    readOnly
                    placeholder="End date"
                    className="w-full text-xs sm:text-sm focus:outline-none bg-transparent"
                  />
                </div>
                <div className="px-2 text-gray-400 flex-shrink-0">
                  <CalendarIcon />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center text-sm sm:text-base">
                  {error}
                </div>
              ) : (
                <table className="w-full min-w-full border-collapse text-xs sm:text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        Date
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        Operation
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        Done By
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="p-8 text-center text-gray-500 bg-gray-50"
                        >
                          No data
                        </td>
                      </tr>
                    ) : (
                      data.map(function (item, index) {
                        const rowBgClass = index % 2 === 0 ? "" : "bg-gray-50";

                        return (
                          <tr key={index} className={rowBgClass}>
                            <td className="p-2 border border-gray-300">
                              <span className="whitespace-nowrap px-1 sm:px-2 block">
                                {item.date || "-"}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span className="whitespace-nowrap px-1 sm:px-2 block">
                                {item.operation || "-"}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span className="whitespace-nowrap px-1 sm:px-2 block">
                                {item.doneBy || "-"}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span className="px-1 sm:px-2 block break-words">
                                {item.description || "-"}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountOperationsReportPage;
